import React, { useEffect } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import {
  Box,
  Grid,
  Theme,
  makeStyles,
  createStyles,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import { TRANSACTIONS_SUMMARY_QUERY, WORKFLOW_LIST, PARTICIPANT_SUMMARY } from '../graphQL/queries';
import { Loading } from '../../Loading';
import { TransactionTypes } from './config';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import DateComp from './DateComp';
import { getTimeIndices } from '../../utils/helpers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      color: theme.palette.background.paper
    },
    label: {
      color: '#000'
    },
    options: {
      padding: '10px',
      marginBottom: '10px'
    },
    select: {
      width: '100%',
      paddingTop: '10px'
    },
    root: {
      margin: '10px 0',
      minHeight: '60vh'
    },
    formControl: {
      minWidth: 240
    },
    dateRange: {
      marginTop: theme.spacing(2)
    }
  })
);

type workflowObj = {
  workflow: workflowContentData;
};

type workflowContentData = {
  content: workflowInfo[];
};

type workflowInfo = {
  workflowCode: string;
  workflowName: string;
  operations?: any;
};

const TransactionsChart = (props: any) => {
  const classes = useStyles();
  NoDataToDisplay(Highcharts);
  const today = new Date();
  const [timeIndices, setTimeIndices] = React.useState<any>([]);
  const [serviceList, setServiceList] = React.useState<any>([]);
  const [participantList, setParticipantList] = React.useState<any>([]);
  const [partcipantType, setPartcipantType] = React.useState<any>(null);
  const [serviceCode, setServiceCode] = React.useState<any>('');
  const [serviceName, setServiceName] = React.useState<any>('');
  const [participantCode, setParticipantCode] = React.useState<any>(null);
  const [finalData, setFinalData] = React.useState<any>([]);
  const date1 = moment(new Date(today.getFullYear(), today.getMonth(), 1)).format('YYYY-MM-DD');
  const date2 = moment(new Date(today.getFullYear(), today.getMonth() + 1, 0)).format('YYYY-MM-DD');

  const [startDate, setStartDate] = React.useState<any>(date1);
  const [endDate, setEndDate] = React.useState<any>(date2);
  const workflowList = useQuery<workflowObj, {}>(WORKFLOW_LIST, {});
  const { data: participantData } = useQuery<any, {}>(PARTICIPANT_SUMMARY, {
    skip:
      props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
      props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
        ? true
        : false
  });

  let [getTransactionSummary, { loading, data }] = useLazyQuery(TRANSACTIONS_SUMMARY_QUERY, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    const dateArray = getTimeIndices(date1, date2);
    setTimeIndices(dateArray);
    const workflowCode = _.map(workflowList?.data?.workflow?.content, 'workflowCode');
    const workflowName = _.map(workflowList?.data?.workflow?.content, 'workflowName');

    const services = workflowCode.reduce((workflowCode: any, code: string, index: number) => {
      workflowCode[workflowName[index]] = code;
      return workflowCode;
    }, {});
    setServiceList(services);
    if (
      props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
      props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
    ) {
      setParticipantList(props?.userData?.userProfile?.participantRoleInfos);
    } else {
      setParticipantList(participantData?.participantInfo?.content || props.participantCode);
    }
    if (!_.isEmpty(services)) {
      setServiceCode(services[Object.keys(services)[0]]);
      setServiceName(Object.keys(services)[0]);
      const variables = {
        startDate: `${startDate} 00:00:00`,
        endDate: `${endDate} 23:59:59`,
        service: services[Object.keys(services)[0]]
      };
      getSummary(variables);
    }
  }, [date1, date2, getTransactionSummary, workflowList, participantData, props]);

  useEffect(() => {
    const statusData: any = [];
    const statusMap: any = new Map();

    if (data?.TransactionsSummary) {
      (data?.TransactionsSummary || []).forEach((obj: any) => {
        let arr: any = {};
        const key = obj.httpResponseStatus;
        if (statusMap.has(key)) {
          arr = statusMap.get(key);
        }
        arr[obj.date] = obj;
        statusMap.set(key, arr);
      });
      for (const status of statusMap.keys()) {
        const seriesObj = {
          type: 'column',
          name: TransactionTypes[status].label,
          color: TransactionTypes[status].color,
          data: timeIndices?.map((date: any) => ({
            y: statusMap.get(status)[date] ? statusMap.get(status)[date].count : 0
          }))
        };
        statusData.push(seriesObj);
      }
    }
    setFinalData(statusData);
  }, [data?.TransactionsSummary, timeIndices]);

  const BarChartOptions: Highcharts.Options = {
    chart: {
      type: 'column',
      backgroundColor: 'none'
    },
    title: {
      text: `Transactions Summary Chart ${serviceName}`
    },
    xAxis: {
      categories: timeIndices,
      labels: {
        style: {
          fontSize: '12px'
        }
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Number of Transactions'
      },
      stackLabels: {
        enabled: true
      },
      labels: {
        style: {
          fontSize: '14px'
        }
      }
    },
    legend: {
      align: 'right',
      x: -30,
      verticalAlign: 'top',
      y: 25,
      floating: true,
      borderColor: '#CCC',
      borderWidth: 1,
      shadow: false
    },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
    },
    credits: {
      enabled: false
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false
        }
      }
    },
    lang: {
      noData: 'No Data to display'
    },
    noData: {
      position: {
        x: 0,
        y: 0,
        align: 'center',
        verticalAlign: 'middle'
      }
    },
    series: finalData
  };

  const getSummary = (variables: any) => {
    const payload = _.omitBy(variables, _.isNil);
    const query = Object.keys(payload)
      .map((item: any) => `${item}=${payload[item]}`)
      .join('&');
    getTransactionSummary({
      variables: { query: query }
    });
  };

  const handleApply = (startDate: any, endDate: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    const dateArray = getTimeIndices(startDate, endDate);
    setTimeIndices(dateArray);
    const variables = {
      startDate: `${startDate} 00:00:00`,
      endDate: `${endDate} 23:59:59`,
      service: serviceCode,
      participantCode: participantCode !== 'allParticipant' ? participantCode : null,
      participantType: participantCode !== 'allParticipant' ? partcipantType : null
    };
    getSummary(variables);
  };

  const handleServiceChange = (e: any) => {
    const { name } = e.currentTarget.dataset;
    const value = e.target.value;
    setServiceCode(value);
    setServiceName(name);
    const variables = {
      startDate: `${startDate} 00:00:00`,
      endDate: `${endDate} 23:59:59`,
      service: value,
      participantCode: participantCode !== 'allParticipant' ? participantCode : null,
      participantType: participantCode !== 'allParticipant' ? partcipantType : null
    };
    getSummary(variables);
  };

  const handleParticipantChange = (e: any) => {
    const { name, value } = e.currentTarget.dataset;
    setPartcipantType(name);
    setParticipantCode(value);
    const variables = {
      startDate: `${startDate} 00:00:00`,
      endDate: `${endDate} 23:59:59`,
      service: serviceCode,
      participantCode: value !== 'allParticipant' ? value : null,
      participantType: value !== 'allParticipant' ? name : null
    };
    getSummary(variables);
  };

  return (
    <Box width="100%" className={classes.root}>
      <Grid container alignItems="center" justify="center" id="transactions-summary-chart">
        <Grid item xs={12} className={classes.options}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <FormControl className={classes.formControl}>
                <InputLabel id="service-list-select" className={classes.label}>
                  Select Service
                </InputLabel>
                <Select
                  value={serviceCode}
                  onChange={handleServiceChange}
                  className={classes.select}
                  id="service-select"
                  labelId="service-list-select"
                >
                  {!_.isEmpty(serviceList) &&
                    Object.keys(serviceList).map((service, index) => (
                      <MenuItem key={`${service}-${index}`} value={serviceList[service]} data-name={service}>
                        {service}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl className={classes.formControl}>
                <InputLabel id="participant-list-select" className={classes.label}>
                  Select Participant
                </InputLabel>
                <Select
                  labelId="participant-list-select"
                  value={
                    (props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
                      props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN') &&
                    props?.userData?.userProfile?.participantRoleInfos?.length === 1
                      ? props?.userData?.userProfile?.participantRoleInfos?.[0]?.participant?.participantCode
                      : participantCode
                  }
                  onChange={handleParticipantChange}
                  disabled={
                    (props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
                      props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN') &&
                    props?.userData?.userProfile?.participantRoleInfos?.length === 1
                  }
                  className={classes.select}
                  id="participant-select"
                >
                  {(props?.userData?.userProfile?.roles?.[0] !== 'PARTICIPANT_SUPPORT' ||
                    props?.userData?.userProfile?.roles?.[0] !== 'PARTICIPANT_ADMIN') && (
                    <MenuItem value="allParticipant" data-name="allParticipant">
                      All Participant
                    </MenuItem>
                  )}
                  {!_.isEmpty(participantList) &&
                    participantList?.length > 0 &&
                    participantList?.map((participant: any, index: number) => (
                      <MenuItem
                        key={`${participant}-${index}`}
                        value={
                          props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
                          props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
                            ? participant?.participant?.participantCode
                            : participant?.participantCode
                        }
                        data-name={
                          props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
                          props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
                            ? participant?.participant?.participantType
                            : participant?.participantType
                        }
                      >
                        {props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_SUPPORT' ||
                        props?.userData?.userProfile?.roles?.[0] === 'PARTICIPANT_ADMIN'
                          ? participant?.participant?.participantCode
                          : participant?.participantCode}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} className={classes.dateRange}>
              <DateComp handleApply={handleApply} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {loading ? <Loading /> : <HighchartsReact highcharts={Highcharts} options={BarChartOptions} />}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransactionsChart;
